import { useAdminMe, useAdmins, useUpdateAdmin, type Admin } from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  PageHeader,
  Switch,
  type DropdownItem,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  MdAdminPanelSettings,
  MdDelete,
  MdEdit,
  MdMoreVert,
  MdPerson,
  MdPersonAdd,
} from 'react-icons/md';
import { useState } from 'react';

import { AdminModal } from '@/features/admins/admin-modal';
import { DeleteAdminModal } from '@/features/admins/delete-admin-modal';
import { PasswordModal } from '@/features/admins/password-modal';
import { errorMessage } from '@/lib/auth';

export const Route = createFileRoute('/_app/admins')({
  /**
   * Réservé aux superadmins : un `admin` qui pourrait créer des comptes ou
   * changer des rôles s'octroierait le superadmin en deux gestes. L'API le
   * refuse déjà, ce garde évite d'afficher un écran qui répondrait 403.
   */
  beforeLoad: ({ context }) => {
    if (context.me.admin.role !== 'superadmin') throw redirect({ to: '/' });
  },
  component: AdministrateursPage,
});

function AdministrateursPage() {
  const { data: me } = useAdminMe();
  const { data, isPending, error } = useAdmins();
  const updateAdmin = useUpdateAdmin();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Admin | null>(null);
  const [deleting, setDeleting] = useState<Admin | null>(null);
  const [genere, setGenere] = useState<{ motDePasse: string; email: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * Raison pour laquelle les actions sont bloquées, ou `null`. Reprend les
   * refus du backend : on ne propose pas un geste qui reviendrait en erreur.
   */
  const raisonBlocage = (admin: Admin): string | null =>
    admin.id === me?.admin.id ? 'Vous ne pouvez pas modifier votre propre accès' : null;

  const toggleActif = async (admin: Admin, actif: boolean) => {
    setActionError(null);
    try {
      await updateAdmin.mutateAsync({ id: admin.id, actif });
    } catch (cause) {
      setActionError(errorMessage(cause, 'Modification impossible.'));
    }
  };

  const columns: TableColumn<Admin>[] = [
    {
      key: 'personne',
      title: 'Administrateur',
      icon: MdPerson,
      cell: (admin) => {
        const nom = `${admin.prenom} ${admin.nom}`.trim();
        return (
          <div className="flex items-center gap-3">
            <Avatar name={nom} size="sm" tone="auto" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">
                {nom}
                {admin.id === me?.admin.id ? (
                  <span className="ml-2 text-xs text-content-subtle">(vous)</span>
                ) : null}
              </span>
              <span className="truncate text-xs text-content-muted">{admin.email}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'role',
      title: 'Rôle',
      icon: MdAdminPanelSettings,
      cell: (admin) =>
        admin.role === 'superadmin' ? (
          <Badge tone="accent">Superadmin</Badge>
        ) : (
          <Badge tone="neutral">Administrateur</Badge>
        ),
    },
    {
      key: 'connexion',
      title: 'Dernière connexion',
      cell: (admin) => <span className="text-content-muted">{dateCourte(admin.lastLoginAt)}</span>,
    },
    {
      key: 'statut',
      title: 'Actif',
      align: 'right',
      cell: (admin) => {
        const blocage = raisonBlocage(admin);
        const enCours = updateAdmin.isPending && updateAdmin.variables?.id === admin.id;

        return (
          <Switch
            checked={admin.actif}
            onChange={(actif) => void toggleActif(admin, actif)}
            label={`${admin.actif ? 'Désactiver' : 'Activer'} ${admin.prenom} ${admin.nom}`}
            hideLabel
            disabled={blocage !== null}
            loading={enCours}
            title={blocage ?? undefined}
            className="justify-end"
          />
        );
      },
    },
    {
      key: 'actions',
      align: 'right',
      className: 'w-px',
      cell: (admin) => {
        const items: DropdownItem[] = [
          { label: 'Modifier', icon: <MdEdit />, onSelect: () => setEditing(admin) },
          // La suppression de soi est refusée côté API ; on ne la propose pas.
          ...(admin.id === me?.admin.id
            ? []
            : [
                {
                  label: 'Supprimer',
                  icon: <MdDelete />,
                  tone: 'danger' as const,
                  onSelect: () => setDeleting(admin),
                },
              ]),
        ];

        return (
          <DropdownMenu
            aria-label={`Actions sur ${admin.prenom} ${admin.nom}`}
            trigger={<MdMoreVert className="size-5" />}
            triggerClassName="flex size-9 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-muted hover:text-content"
            items={items}
          />
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdAdminPanelSettings />}
        title="Administrateurs"
        description="Les accès à la console Fidalia."
        actions={
          <Button icon={<MdPersonAdd className="size-5" />} onClick={() => setCreating(true)}>
            Nouvel administrateur
          </Button>
        }
      />

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}
      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={columns}
        isLoading={isPending}
        rowId={(admin) => admin.id}
        rowClassName={(admin) => (admin.actif ? '' : 'opacity-60')}
        emptyMessage="Aucun administrateur."
      />

      <AdminModal
        open={creating}
        onClose={() => setCreating(false)}
        onPasswordGenerated={(motDePasse, email) => setGenere({ motDePasse, email })}
      />

      {/* `key` force un formulaire neuf : sans elle, les valeurs par défaut du
          précédent resteraient affichées. */}
      {editing ? (
        <AdminModal key={editing.id} open admin={editing} onClose={() => setEditing(null)} />
      ) : null}

      <DeleteAdminModal admin={deleting} onClose={() => setDeleting(null)} />

      <PasswordModal
        motDePasse={genere?.motDePasse ?? null}
        email={genere?.email ?? ''}
        onClose={() => setGenere(null)}
      />
    </div>
  );
}

/** `null` affiché en toutes lettres plutôt qu'en « Invalid Date ». */
function dateCourte(value: string | null): string {
  if (!value) return 'Jamais connecté';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
